import React, { useEffect, useMemo, useState } from 'react';
import { useForm, standardSchemaResolver } from '@fiverr-private/forms';
import { raiseToast } from '@fiverr-private/toast';
import { resetPassword } from '../../../../services';
import { translate } from '../../../../utils/translate';
import { raiseErrorToast } from '../../../../utils/raiseErrorToast';
import { globalMixpanelReporter, MixpanelEventsContext } from '../../../../events/mixpanel';
import { globalBigQueryReporter, BigQueryEventsContext } from '../../../../events/bigQuery';
import { globalGrafanaReporter } from '../../../../events/grafana';
import { useOnceBeforeRenderOnClient } from '../../../../hooks/useOnceBeforeRenderOnClient';
import { Variant } from '../../../../shared/constants';
import { createSchema, FormSchema } from './ResetPasswordStepForm.constants';
import { CheckInbox } from './CheckInbox';
import { InitialBody } from './InitialBody';

const handleSubmitEvents = () => {
    globalMixpanelReporter.send('Identification - Reset Password - Submit');
    globalBigQueryReporter.send('identification.reset_password.submit');
    globalGrafanaReporter.count('identification.%{platform}.reset_password.submit');
};

const handleFailEvents = () => {
    globalMixpanelReporter.send('Identification - Reset Password - Failed');
    globalBigQueryReporter.send('identification.reset_password.failed');
    globalGrafanaReporter.count('identification.%{platform}.reset_password.failed');
};

const handleSuccessEvents = () => {
    globalMixpanelReporter.send('Identification - Reset Password - Completed');
    globalBigQueryReporter.send('identification.reset_password.completed');
    globalGrafanaReporter.count('identification.%{platform}.reset_password.completed');
};

interface ResetPasswordStepFormProps {
    onSubmit?: ({ redirectUrl }: { redirectUrl?: string }) => void;
    handleDoneClick?: () => void;
    email?: string;
    mixpanelEnrichment?: MixpanelEventsContext;
    bigQueryEnrichment?: BigQueryEventsContext;
    doubleStepResetPasswordExperimentGroup?: number;
    variant?: Variant;
    goBack?: () => void;
}

export const ResetPasswordStepForm = ({
    handleDoneClick,
    onSubmit,
    mixpanelEnrichment = {},
    bigQueryEnrichment = {},
    email = '',
    doubleStepResetPasswordExperimentGroup = 1,
    variant,
    goBack,
}: ResetPasswordStepFormProps) => {
    const [showCheckInboxBody, setShowCheckInboxBody] = useState(false);

    const schema = useMemo(() => createSchema(), []);

    const methods = useForm<FormSchema>({
        resolver: standardSchemaResolver(schema),
        defaultValues: { email },
    });

    const isControlGroup = doubleStepResetPasswordExperimentGroup === 1;

    const handleSubmit = async ({ email }: FormSchema) => {
        handleSubmitEvents();

        const { status, redirectUrl, message, statusCode } = await resetPassword({ email, funnel: 'standard' });

        if (statusCode !== 200) {
            handleFailEvents();
            raiseErrorToast(translate('steps.reset_password_success.toast_failure'));
            return;
        }

        if (status === 'success') {
            handleSuccessEvents();

            if (isControlGroup) {
                onSubmit?.({ redirectUrl });
                return;
            }
        }

        if (status === 'failure') {
            handleFailEvents();
        }

        if (message && isControlGroup) {
            methods.setError('email', { message: translate(`validation.email.${message}`) });
        } else {
            raiseToast({
                type: 'success',
                message: translate('steps.reset_password_success.toast_initial_success'),
                location: 'top',
            });
            setShowCheckInboxBody(true);
        }
    };

    useOnceBeforeRenderOnClient(() => {
        globalMixpanelReporter.enrichContext(mixpanelEnrichment);
        globalBigQueryReporter.enrichContext(bigQueryEnrichment);
    });

    useEffect(() => {
        globalMixpanelReporter.send('Identification - Reset Password - Show');
        globalBigQueryReporter.send('identification.reset_password.show');
        globalGrafanaReporter.count('identification.%{platform}.reset_password.show');
    }, []);

    return showCheckInboxBody ? (
        <CheckInbox
            email={methods.getValues('email')}
            variant={variant}
            goBack={goBack}
            handleDoneClick={handleDoneClick}
        />
    ) : (
        <InitialBody methods={methods} handleSubmit={handleSubmit} variant={variant} goBack={goBack} />
    );
};
